/**
 * a2hs.ts — "홈 화면에 추가"(Add to Home Screen) 안내·알림 켜기 안내의 순수 판정 (firebase 의존 없음).
 *
 * 흐름(2026-11-02 결정):
 *   ① 모바일 브라우저에서 로그인 → 설치 안내 모달(아이폰 = 공유 → 홈 화면에 추가 → 추가, 안드로이드 = 버튼 한 번)
 *   ② 홈 화면 앱(standalone)으로 처음 들어오면 → 알림 켜기 안내 모달(아이폰은 이 자리에서만 푸시 가능)
 *   ③ "다시 보지 않기"를 눌러도 내 계정 › 기기관리 "홈 화면 앱" 줄에서 다시 열 수 있다.
 *
 * 저장은 기기(브라우저) 단위 localStorage — `lib/a2hsLocal.ts`. 여기는 판정식만.
 */

export type A2hsPlatform = "ios" | "android";

export interface A2hsEnv {
  userAgent: string;
  maxTouchPoints?: number;
  /** 이미 홈 화면 앱으로 열렸는지 */
  standalone: boolean;
}

/**
 * 설치 안내를 보여 줄 플랫폼. null = 안내 불가(이미 앱 · PC · 인앱 브라우저 등).
 *  - iOS: Safari 에서만 "홈 화면에 추가"가 있다. Chrome(CriOS)·카카오톡·네이버 앱 안에서는 못 한다.
 *  - Android: Chrome·삼성 인터넷·Edge·Whale 만. 앱 안 WebView(`; wv)`)·카카오톡·네이버 앱 제외.
 */
export function a2hsPlatformOf(env: A2hsEnv): A2hsPlatform | null {
  if (env.standalone) return null;
  const ua = env.userAgent || "";
  if (/KAKAOTALK|NAVER\(inapp|Instagram|FBAN|FBAV|Line\//i.test(ua)) return null;
  const isIos = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1);
  if (isIos) {
    if (/CriOS|FxiOS|EdgiOS|OPT\/|Whale\//.test(ua)) return null;
    return /Safari\//.test(ua) ? "ios" : null;
  }
  if (/Android/.test(ua)) {
    if (/; wv\)/.test(ua)) return null;
    return /Chrome\/|SamsungBrowser\/|Edg\/|Whale\//.test(ua) ? "android" : null;
  }
  return null;
}

/** 설치 안내를 닫은 기록 — never = 다시 보지 않기, later = 이 시각까지 미루기 */
export type A2hsDismiss = { kind: "never" } | { kind: "later"; until: number } | null;

const DAY_MS = 24 * 60 * 60 * 1000;
/** "나중에" — 7일 뒤 다시 */
export const A2HS_LATER_MS = 7 * DAY_MS;
/** 아이폰 "알겠어요, 지금 추가할게요" — 설치 여부를 알 수 없으니 3일 뒤 다시(그 사이 앱으로 들어오면 안 뜸) */
export const A2HS_IOS_DONE_MS = 3 * DAY_MS;

/** 저장값(JSON 문자열) → 검증된 기록. 깨진 값은 null(= 안내함) */
export function parseA2hsDismiss(raw: string | null): A2hsDismiss {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { kind?: unknown; until?: unknown };
    if (v?.kind === "never") return { kind: "never" };
    if (v?.kind === "later" && typeof v.until === "number" && Number.isFinite(v.until)) return { kind: "later", until: v.until };
  } catch {
    /* 깨진 값 */
  }
  return null;
}

export function shouldOfferInstall(platform: A2hsPlatform | null, dismiss: A2hsDismiss, nowMs: number): boolean {
  if (!platform) return false;
  if (!dismiss) return true;
  if (dismiss.kind === "never") return false;
  return nowMs >= dismiss.until;
}

export interface NotifyOfferInput {
  /** 홈 화면 앱으로 열렸는지 — 아이폰은 이때만 푸시 가능 */
  standalone: boolean;
  /** 모바일 기기인지(PC 설치형 PWA 제외) */
  mobile: boolean;
  /** 공용 PC 로그인(기기 미등록)이면 푸시를 켤 수 없다 */
  sharedSession: boolean;
  /** 이 기기에서 이미 안내를 봤는지 */
  seen: boolean;
  /** 웹 푸시 지원 여부 */
  supported: boolean;
  /** 브라우저 알림 권한 — "default" 일 때만 물어볼 가치가 있다 */
  permission: "default" | "granted" | "denied" | "unsupported";
}

/** 알림 켜기 안내는 홈 화면 앱 첫 진입 1회 — 권한을 아직 안 물어본 상태에서만 */
export function shouldOfferNotify(i: NotifyOfferInput): boolean {
  return i.standalone && i.mobile && !i.sharedSession && !i.seen && i.supported && i.permission === "default";
}

/** 알림 안내 플랫폼 그림 — iOS 외는 안드로이드 그림 */
export function notifyDemoPlatformOf(userAgent: string, maxTouchPoints = 0): A2hsPlatform {
  const ua = userAgent || "";
  const isIos = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  return isIos ? "ios" : "android";
}
